import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "About", id: "about" },
  { label: "The MNE Formula", id: "formula" },
  { label: "Services", id: "services" },
  { label: "Classes", id: "group-classes" },
  { label: "Testimonials", id: "testimonials" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-cream/80 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center"
          aria-label="Jennie-Marie home"
        >
          <img src="/assets/jennie-marie-logo.png" alt="Jennie-Marie" className="h-8 md:h-9 w-auto object-contain" />
        </button>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => go(l.id)}
              className="group relative text-[13px] uppercase tracking-[0.18em] text-stone hover:text-plum transition-colors duration-300"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-plum transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <button
          data-testid="nav-book-cta"
          onClick={() => go("contact")}
          className="hidden md:inline-flex items-center rounded-full bg-plum px-6 py-3 text-[12px] uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-plum-deep"
        >
          Book a Call
        </button>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-plum"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-b border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-2xl text-plum"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-book"
                onClick={() => go("contact")}
                className="mt-2 rounded-full bg-plum px-6 py-3 text-[12px] uppercase tracking-[0.18em] text-cream"
              >
                Book a Call
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
